import React from "react";
import Grid from "@mui/material/Grid";

import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";

/**
 * RffFormSection
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Content of the form section
 * @param {string} [props.title] - Section title (optional)
 * @param {string} [props.label] - Section label (optional)
 * @param {string} [props.titleType='h4'] - Heading type for the title (e.g. 'h1', 'h2', 'h3', 'h4') if Htag supports it
 * @param {boolean} [props.disableGrid=false] - If true, content won't be rendered in a MUI Grid container
 * @param {boolean} [props.isGridChild=false] - If true, wraps the section in a <Grid item xs={12}/> so it can be placed within a Grid container
 * @param {number} [props.columnSpacing=2] - The spacing between columns in the Grid
 * @param {number} [props.rowSpacing=3.2] - The spacing between rows in the Grid
 * @param {object} [props.sx] - Optional style overrides (MUI sx prop)
 * @param {boolean} [props.suppressBottomMargin] - If true, suppresses the bottom margin
 */
const RffRowGroup = ({
  children,
  title,
  suppressBottomMargin = true,
  titleType = "h2",
  size = 12,
  disableGrid = false,
  isGridChild = false,
  suppressBottomDivider = false,
  columnSpacing = 2.4,
  rowSpacing = 0.4,
  label,
  groupLabel,
  // columnSpacing = 2,
  // rowSpacing = 3.2,
  sx,
  contentSx,
}) => {
  const content = (
    <ContentWrapper
      title={title}
      titleType={titleType}
      disableGrid={disableGrid}
      columnSpacing={columnSpacing}
      suppressBottomMargin={suppressBottomMargin}
      rowSpacing={rowSpacing}
      label={label}
      suppressBottomDivider={suppressBottomDivider}
      groupLabel={groupLabel}
      sx={sx}
      contentSx={contentSx}
    >
      {children}
    </ContentWrapper>
  );

  // If this section is part of a larger Grid, wrap it in <Grid item xs={12}/>
  if (isGridChild) {
    return (
      <Grid item xs={size}>
        {content}
      </Grid>
    );
  }

  // Otherwise, just render the content directly
  return content;
};

const ContentWrapper = ({
  title,
  titleType,
  disableGrid,
  columnSpacing,
  rowSpacing,
  sx,
  suppressBottomMargin,
  contentSx,
  children,
  label,
  groupLabel,
  suppressBottomDivider,
}) => {
  return (
    <Flx
      column
      grow
      fw
      className={suppressBottomMargin ? "" : "rff-group"}
      sx={{
        label: {
          lineHeight: 1.5,
          fontSize: 11.5,
          color: "#64748b",
          // color: "#94a3b8",
        },
        "input , .MuiInputBase-input, .MuiTypography-root": {
          lineHeight: 1.5,
          fontSize: 11.5,
          // color: "#94a3b8",
        },
        ".MuiAutocomplete-root": {
          width: "100%",
        },
        ".input-row-root , .input-wrapper-root": {
          borderBottom: "1px dashed #dde0e4b5",
          py: "6px",
        },
        ...(suppressBottomDivider && {
          [disableGrid
            ? "& .input-row-root:last-child, & .input-wrapper-root:last-child"
            : ".MuiGrid-item:last-child .input-row-root, .MuiGrid-item:last-child .input-wrapper-root"]: {
            borderBottom: "none",
          },
        }),
        ".MuiGrid-root": {
          py: "0px",
        },

        ...sx,
      }}
    >
      {(label || groupLabel) && (
        <Txt
          sx={{
            fontWeight: 600,
            fontSize: "14px",
            color: "main.secondary",
            lineHeight: 1.6,
            mb: 1,
          }}
        >
          {label || groupLabel}
        </Txt>
      )}
      <SectionContent
        disableGrid={disableGrid}
        columnSpacing={columnSpacing}
        rowSpacing={rowSpacing}
        sx={contentSx}
      >
        {children}
      </SectionContent>
    </Flx>
  );
};

const SectionContent = ({
  disableGrid,
  columnSpacing,
  rowSpacing,
  sx,

  children,
}) => {
  if (disableGrid) {
    return (
      <Flx column fw sx={{ gap: "14px", ...sx }}>
        {children}
      </Flx>
    );
  }

  return (
    <Grid
      container
      columnSpacing={columnSpacing}
      rowSpacing={rowSpacing}
      sx={sx}
    >
      {children}
    </Grid>
  );
};

export default RffRowGroup;
